import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { UserCheck, UserMinus, UsersRound } from "lucide-react";
import { getTenantRegistrationReportAction } from "../../Actions/ReportsAction";
import CustomSnackbar from "../Common/snackBar";
import ReportKpiCards from "./ReportKpiCards";
import {
    ChartPanel,
    formatCompact,
    PORTAL_COLORS,
    rechartsAxis,
    rechartsGrid,
    ReportTooltip,
} from "./reportCharts";

const TenantRegistrationReport = () => {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({
        totalTenants: 0,
        activeTenants: 0,
        inactiveTenants: 0,
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [monthlyRegistrations, setMonthlyRegistrations] =
        useState([]);

    const fetchReport = async () => {
        try {
            setLoading(true);

            const response =
                await getTenantRegistrationReportAction();

            setSummary(response?.data?.summary || {});

            setMonthlyRegistrations(
                response?.data?.monthlyRegistrations || []
            );
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const monthlyChartData = monthlyRegistrations.map((item) => ({
        name: item.month,
        value: Number(item.count || 0),
    }));

    return (
        <>
            <Grid container spacing={3}>
                {/* Summary Cards */}
                <Grid item xs={12}>
                    <ReportKpiCards
                        loading={loading}
                        cards={[
                            {
                                key: "total",
                                label: "Total Registrations",
                                value: summary.totalTenants ?? 0,
                                accent: "#22419e",
                                icon: <UsersRound />,
                            },
                            {
                                key: "active",
                                label: "Active Tenants",
                                value: summary.activeTenants ?? 0,
                                accent: "#059669",
                                icon: <UserCheck />,
                            },
                            {
                                key: "inactive",
                                label: "Inactive Tenants",
                                value: summary.inactiveTenants ?? 0,
                                accent: "#dc2626",
                                icon: <UserMinus />,
                            },
                        ]}
                    />
                </Grid>

                {/* Monthly Registrations */}
                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0 }}>
                    <Grid item xs={12}>
                        <ChartPanel
                            title="Monthly registrations"
                            caption="Tenants onboarded per month"
                            height={330}
                            loading={loading}
                            isEmpty={monthlyChartData.length === 0}
                            emptyLabel="No registrations recorded yet"
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={monthlyChartData} barCategoryGap="38%">
                                    <CartesianGrid {...rechartsGrid} />
                                    <XAxis dataKey="name" {...rechartsAxis} />
                                    <YAxis
                                        {...rechartsAxis}
                                        width={40}
                                        tickFormatter={formatCompact}
                                    />
                                    <Tooltip
                                        content={<ReportTooltip />}
                                        cursor={{ fill: "rgba(34, 65, 158, 0.05)" }}
                                    />
                                    <Bar
                                        dataKey="value"
                                        fill={PORTAL_COLORS.qbot}
                                        radius={[8, 8, 0, 0]}
                                        maxBarSize={46}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartPanel>
                    </Grid>
                </Grid>
            </Grid>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default TenantRegistrationReport;
